import { ControlledExport } from '../components/ControlledExport';
import { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { motion } from 'motion/react';
import {
  Users,
  ArrowUpRight,
  ArrowRight,
  Plus,
  Search,
  SlidersHorizontal,
  Building2,
  Sparkles,
  CalendarDays,
  Clock,
  Check,
  CheckCircle2,
  ChevronRight,
  MapPin,
  BedDouble,
  Maximize,
  Heart,
  LayoutGrid,
  List,
  Download,
  Upload,
  TrendingUp,
  Target,
  IndianRupee,
  Phone,
  MoreHorizontal,
  Flag,
  ShieldCheck,
  X,
  RefreshCw,
  Loader2,
  GripVertical,
  Filter,
  Sun,
  Compass,
  Pin,
  PinOff,
} from 'lucide-react';
import { useCRM } from '../lib/context';
import { canWriteEntity, canManageWorkspace } from '../lib/permissions';
import { api, money, dateLabel, initials, today } from '../lib/api';
import { contactBudgetLabel } from '../lib/contact-budget';
import { stages, type Entity } from '../lib/types';
import {
  PageHeader,
  Panel,
  Badge,
  Avatar,
  EmptyState,
  Tabs,
  StatCard,
  SectionHeading,
  Score,
} from '../components/ui';
import { Modal } from '../components/Modal';
import { LuxuryScene } from '../components/LuxuryScene';
import { parseCsvRows, mapCsvColumns } from '../lib/csv';
import { InventorySyncStatus } from '../components/SourceSyncStatus';
import { PropertySourceDetails } from '../components/PropertySourceDetails';
import {
  propertyPriceLabel,
  propertyAreaLabel,
  propertyStatusLabel,
  isArchivedProperty,
  isReadyProperty,
} from '../lib/inventory';
const OffersPanel = lazy(() => import('./Relationships').then((m) => ({ default: m.OffersPanel })));

export function Overview() {
  const { data, navigate, openContact, openCreate, mutate, notify } = useCRM();
  const priorities = [...data.contacts]
    .filter(
      (c) => !c.suppressed && !['Closed Won', 'Closed Lost', 'Disqualified'].includes(c.stage),
    )
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
  const active = data.opportunities.filter(
    (o) => !['Closed Won', 'Closed Lost', 'Disqualified'].includes(o.stage),
  );
  const value = active.reduce((s, o) => s + o.value, 0);
  const tasks = data.tasks
    .filter((t) => !t.completed)
    .sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt))
    .slice(0, 4);
  const won = data.opportunities.filter((o) => o.stage === 'Closed Won');
  const totalRevenue = won.reduce((s, o) => s + o.expectedRevenue, 0);
  const pipelineGroups = [
    {
      label: 'Early pipeline',
      stages: ['Qualified', 'Validated', 'New', 'Engaged'],
      color: '#aec0f6',
    },
    { label: 'Engaged', stages: ['Advisor Engaged', 'Nurture'], color: '#7894eb' },
    { label: 'Site visit', stages: ['Site Visit'], color: '#4b70e8' },
    { label: 'Negotiation', stages: ['Negotiation'], color: '#2456ff' },
    { label: 'Closed won', stages: ['Closed Won'], color: '#c6b187' },
  ];
  const chart = pipelineGroups.map((g) => ({
    ...g,
    count: data.opportunities.filter((o) => g.stages.includes(o.stage)).length,
    value: data.opportunities
      .filter((o) => g.stages.includes(o.stage))
      .reduce((s, o) => s + o.value, 0),
  }));
  return (
    <>
      <PageHeader
        eyebrow={new Intl.DateTimeFormat('en-IN', {
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          timeZone: 'Asia/Kolkata',
        })
          .format(new Date())
          .toUpperCase()}
        title={`Every connection. Elevated.`}
        description={`${data.user.name ? `Welcome back, ${data.user.name.split(' ')[0]}.` : 'Welcome to your workspace.'} Here's where your next opportunity begins.`}
        action={
          <>
            <button className="btn" onClick={() => navigate('analytics')}>
              <CalendarDays size={15} />
              Workspace overview
              <ChevronDownIcon />
            </button>
            <button
              className="btn btn-primary"
              disabled={!canWriteEntity(data.user, 'contacts')}
              onClick={() => openCreate('contacts')}
            >
              <Plus size={16} />
              New relationship
            </button>
          </>
        }
      />
      <div className="stats-grid">
        <StatCard
          label="Active relationships"
          value={data.contacts
            .filter((c) => !c.suppressed)
            .length.toString()
            .padStart(2, '0')}
          icon={Users}
          detail={`${data.contacts.filter((c) => c.score >= 85 && !c.suppressed && !['Closed Won', 'Closed Lost', 'Disqualified'].includes(c.stage)).length} high-priority buyers`}
          trend="In focus"
        />
        <StatCard
          label="Open pipeline value"
          value={money(value)}
          icon={TrendingUp}
          detail={`${active.length} opportunities in motion`}
        />
        <StatCard
          label="Upcoming site visits"
          value={data.visits
            .filter((v) => ['Scheduled', 'Confirmed'].includes(v.status) && v.date >= today())
            .length.toString()
            .padStart(2, '0')}
          icon={CalendarDays}
          detail="Personal connections, on the ground"
        />
        <StatCard
          label="Closed-won commission"
          value={money(totalRevenue)}
          icon={IndianRupee}
          detail={`Expected commission · ${won.length} closed deals`}
          trend="Closed won"
        />
      </div>
      <div className="overview-middle">
        <section className="intelligence-card">
          <div className="intelligence-noise" />
          <div className="intelligence-copy">
            <div className="intelligence-label">
              <span>
                <Sparkles size={13} />
              </span>{' '}
              YOUR INTELLIGENCE BRIEF <span className="live-pill">CRM DATA</span>
            </div>
            <h2>
              A new perspective.
              <br />
              On possibility.
            </h2>
            <p>
              {
                data.contacts.filter(
                  (c) =>
                    c.score >= 85 &&
                    !c.suppressed &&
                    !['Closed Won', 'Closed Lost', 'Disqualified'].includes(c.stage),
                ).length
              }{' '}
              contacts have a recorded priority score of 85 or higher. Review their requirements
              before planning the next conversation.
            </p>
            <button className="btn btn-cream" onClick={() => navigate('matches')}>
              Explore buyer matches <ArrowUpRight size={16} />
            </button>
            <div className="brief-footer">
              <span className="mini-avatar-stack">
                {priorities.slice(0, 3).map((c) => (
                  <Avatar key={c.id} name={c.name} size={23} />
                ))}
              </span>
              <span>Based on recorded requirements and activity</span>
            </div>
          </div>
          <LuxuryScene variant="hero" />
        </section>
        <Panel className="pipeline-summary">
          <SectionHeading
            title="A pipeline of possibilities"
            subtitle="Every stage, a step closer."
            action={
              <button
                className="icon-btn"
                aria-label="Open pipeline"
                onClick={() => navigate('pipeline')}
              >
                <ArrowUpRight size={18} />
              </button>
            }
          />
          <div className="pipeline-total">
            {money(value)}
            <span>in active opportunities</span>
          </div>
          <div className="pipeline-bar">
            {chart
              .filter((g) => g.count)
              .map((g) => (
                <motion.div
                  key={g.label}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  style={{ flex: g.count, background: g.color }}
                  title={`${g.label}: ${g.count} opportunities`}
                />
              ))}
          </div>
          <div className="pipeline-legend">
            {chart.map((g) => (
              <button key={g.label} onClick={() => navigate('pipeline')}>
                <span>
                  <i style={{ background: g.color }} />
                  {g.label}
                </span>
                <strong>{g.count.toString().padStart(2, '0')}</strong>
                <span>{money(g.value)}</span>
              </button>
            ))}
          </div>
        </Panel>
      </div>
      <div className="overview-lower">
        <Panel className="priority-panel">
          <SectionHeading
            title="Relationships that deserve your time"
            subtitle="Your highest-scoring open relationships."
            action={
              <button className="text-link" onClick={() => navigate('contacts')}>
                View all <ArrowRight size={14} />
              </button>
            }
          />
          <div className="table-wrap">
            <table className="data-table priority-table">
              <thead>
                <tr>
                  <th>RELATIONSHIP</th>
                  <th>PREFERENCE</th>
                  <th>BUDGET</th>
                  <th>BUYER SCORE</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {priorities.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openContact(c)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') openContact(c);
                    }}
                  >
                    <td>
                      <div className="person-cell">
                        <Avatar name={c.name} />
                        <div>
                          <strong>{c.name}</strong>
                          <small>{c.company || c.source}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span>{c.localities?.[0] || 'Exploring'}</span>
                        <small>{c.configuration}</small>
                      </div>
                    </td>
                    <td className="nowrap">{contactBudgetLabel(c)}</td>
                    <td>
                      <Score value={c.score} />
                    </td>
                    <td>
                      <ArrowUpRight size={16} className="row-arrow" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!priorities.length && (
            <EmptyState
              title="No open relationships recorded"
              description="Add a real enquiry or review an authorized source import to build this view."
            />
          )}
        </Panel>
        <Panel className="today-panel">
          <SectionHeading
            title="A little focus goes a long way"
            subtitle="Your next few moves."
            action={<Badge>{tasks.length} tasks</Badge>}
          />
          <div className="today-tasks">
            {tasks.map((t, i) => (
              <div className="today-task" key={t.id}>
                <button
                  className="task-checkbox"
                  aria-label={`Complete ${t.title}`}
                  disabled={!canWriteEntity(data.user, 'tasks', t)}
                  onClick={async () => {
                    try {
                      await mutate(`/tasks/${t.id}`, 'PATCH', {
                        completed: true,
                        version: t.version,
                      });
                      notify('One more thoughtful move, done.');
                    } catch {}
                  }}
                >
                  <Check size={12} />
                </button>
                <button
                  className="today-task-main"
                  onClick={() => {
                    const c = data.contacts.find((c) => c.id === t.contactId);
                    c ? openContact(c) : navigate('tasks');
                  }}
                >
                  <strong>{t.title}</strong>
                  <span>
                    <Clock size={11} />
                    {dateLabel(t.dueAt)}
                    <i />
                    {t.owner?.split(' ')[0]}
                  </span>
                </button>
                <span className={`priority-dot priority-${t.priority}`} />
              </div>
            ))}
          </div>
          {!tasks.length && <p className="muted">No pending tasks recorded.</p>}
          <button
            className="subtle-add"
            disabled={!canWriteEntity(data.user, 'tasks')}
            onClick={() => openCreate('tasks')}
          >
            <Plus size={15} />
            Add a task
          </button>
        </Panel>
      </div>
      <section className="portfolio-preview">
        <SectionHeading
          title="Extraordinary spaces. Exceptional possibilities."
          subtitle="A selection from your property portfolio."
          action={
            <button className="text-link" onClick={() => navigate('properties')}>
              Explore portfolio <ArrowRight size={14} />
            </button>
          }
        />
        <div className="featured-properties">
          {data.properties.slice(0, 3).map((p) => (
            <button className="featured-property" key={p.id} onClick={() => navigate('properties')}>
              <div className="featured-image">
                <PropertyImage property={p} />
                <span>
                  <ArrowUpRight size={16} />
                </span>
              </div>
              <div>
                <span className="eyebrow">{p.locality}</span>
                <h3>{p.name}</h3>
                <p>
                  {p.bhk} <i /> {propertyAreaLabel(p)}
                  <strong>{propertyPriceLabel(p)}</strong>
                </p>
              </div>
            </button>
          ))}
        </div>
        {!data.properties.length && (
          <EmptyState
            icon={Building2}
            title="No properties recorded"
            description="Add your available inventory to begin matching enquiries."
          />
        )}
      </section>
    </>
  );
}
function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
export function PropertyImage({ property }: { property: Entity }) {
  const [failed, setFailed] = useState(false);
  return !failed && property.image ? (
    <img src={property.image} alt={property.name} onError={() => setFailed(true)} loading="lazy" />
  ) : (
    <div className={`property-fallback fallback-${property.locality?.length % 3}`}>
      <div className="fallback-building" />
      <div className="fallback-building second" />
      <span>{property.locality}</span>
    </div>
  );
}

export function ContactsPage() {
  const { data, openContact, openCreate, notify } = useCRM();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('All relationships');
  const [owner, setOwner] = useState('all');
  const [importOpen, setImportOpen] = useState(false);
  const [exportReview, setExportReview] = useState(false);
  const contacts = data.contacts.filter(
    (c) =>
      `${c.name} ${c.company} ${c.email} ${c.phone} ${c.notes} ${c.tags?.join(' ')} ${c.localities?.join(' ')}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (tab === 'All relationships' ||
        (tab === 'High priority' &&
          c.score >= 85 &&
          !c.suppressed &&
          !['Closed Won', 'Closed Lost', 'Disqualified'].includes(c.stage)) ||
        (tab === 'First-party' && c.sourceClass === 'first_party') ||
        (tab === 'Suppressed' && c.suppressed)) &&
      (owner === 'all' || c.owner === owner),
  );
  return (
    <>
      <PageHeader
        eyebrow="YOUR NETWORK, INTELLIGENTLY CONNECTED"
        title="Relationships"
        description="Every conversation is the beginning of something extraordinary."
        action={
          <>
            <button
              className="btn"
              disabled={!canWriteEntity(data.user, 'contacts')}
              onClick={() => setImportOpen(true)}
            >
              <Upload size={15} />
              Import
            </button>
            <button
              className="btn btn-primary"
              disabled={!canWriteEntity(data.user, 'contacts')}
              onClick={() => openCreate('contacts')}
            >
              <Plus size={16} />
              New relationship
            </button>
          </>
        }
      />
      <div className="metric-strip">
        <div>
          <strong>{data.contacts.length}</strong>
          <span>Total relationships</span>
        </div>
        <div>
          <strong>
            {
              data.contacts.filter(
                (c) =>
                  c.score >= 85 &&
                  !c.suppressed &&
                  !['Closed Won', 'Closed Lost', 'Disqualified'].includes(c.stage),
              ).length
            }
          </strong>
          <span>High priority</span>
        </div>
        <div>
          <strong>{data.contacts.filter((c) => c.sourceClass === 'first_party').length}</strong>
          <span>First-party connections</span>
        </div>
        <div>
          <strong>{data.contacts.filter((c) => c.suppressed).length}</strong>
          <span>Suppressed</span>
        </div>
      </div>
      <Panel className="records-panel">
        <div className="records-toolbar">
          <Tabs
            items={['All relationships', 'High priority', 'First-party', 'Suppressed']}
            value={tab}
            onChange={setTab}
          />
          <button
            className="btn btn-sm"
            disabled={!canManageWorkspace(data.user.role)}
            onClick={() => setExportReview(true)}
          >
            <Download size={14} />
            Export
          </button>
        </div>
        <div className="table-filters">
          <div className="search-field">
            <Search size={16} />
            <input
              aria-label="Search relationships"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a name, company or locality..."
            />
            {query && (
              <button aria-label="Clear search" onClick={() => setQuery('')}>
                <X size={14} />
              </button>
            )}
          </div>
          <select
            aria-label="Filter by advisor"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
          >
            <option value="all">All advisors</option>
            {Array.from(new Set(data.contacts.map((c) => c.owner))).map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
          <span className="result-count">{contacts.length} relationships</span>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>RELATIONSHIP</th>
                <th>BUYER SCORE</th>
                <th>BUDGET & PREFERENCE</th>
                <th>STAGE</th>
                <th>ADVISOR</th>
                <th>SOURCE</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} onClick={() => openContact(c)}>
                  <td>
                    <div className="person-cell">
                      <Avatar name={c.name} size={39} />
                      <div>
                        <strong>{c.name}</strong>
                        <small>{c.company || c.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    {c.suppressed ? (
                      <Badge tone="red">Suppressed</Badge>
                    ) : (
                      <Score value={c.score} />
                    )}
                  </td>
                  <td>
                    <div className="cell-stack">
                      <strong>{contactBudgetLabel(c)}</strong>
                      <small>
                        {c.localities?.join(', ')} · {c.configuration}
                      </small>
                    </div>
                  </td>
                  <td>
                    <Badge
                      tone={
                        ['Negotiation', 'Closed Won'].includes(c.stage)
                          ? 'green'
                          : c.stage === 'Site Visit'
                            ? 'blue'
                            : 'gray'
                      }
                      dot
                    >
                      {c.stage}
                    </Badge>
                  </td>
                  <td>
                    <div className="advisor-cell">
                      <Avatar name={c.owner || 'Unassigned'} size={25} />
                      <span>{c.owner?.split(' ')[0] || 'Unassigned'}</span>
                    </div>
                  </td>
                  <td>
                    <span className="source-cell">{c.source}</span>
                  </td>
                  <td>
                    <button aria-label={`Open ${c.name}`} className="icon-btn">
                      <ArrowUpRight size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!contacts.length && (
          <EmptyState
            title="Room for a new connection"
            description="Try another filter or create your first relationship."
          />
        )}
        <div className="table-footer">
          <span>
            Showing {contacts.length} of {data.contacts.length} relationships
          </span>
          <span>
            <ShieldCheck size={13} />
            Source-aware. Permission-first.
          </span>
        </div>
      </Panel>
      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} />}
      {exportReview && (
        <ControlledExport ids={contacts.map((c) => c.id)} onClose={() => setExportReview(false)} />
      )}
    </>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const { mutate, notify } = useCRM();
  const [raw, setRaw] = useState('name,email,phone,company\n');
  const [source, setSource] = useState('unknown');
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sourceName, setSourceName] = useState('CSV import');
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const currentInput = useRef('');
  const fileRead = useRef(0);
  currentInput.current = JSON.stringify([raw, source, sourceName, columnMap]);
  const importFields = [
    'name',
    'email',
    'phone',
    'company',
    'localities',
    'budgetMin',
    'budgetMax',
    'configuration',
    'notes',
  ];
  const headers = useMemo(() => {
    try {
      return parseCsvRows(raw)[0] || [];
    } catch {
      return [];
    }
  }, [raw]);
  const headerKey = headers.join('\u0000');
  useEffect(() => {
    const aliases: Record<string, string[]> = {
      name: ['name', 'fullname', 'displayname', 'contactname'],
      email: ['email', 'emailaddress', 'primaryemail'],
      phone: ['phone', 'mobile', 'phonenumber', 'mobilenumber'],
      company: ['company', 'organization', 'organisation'],
      localities: ['localities', 'locality', 'preferredlocalities'],
      budgetMin: ['budgetmin', 'minbudget'],
      budgetMax: ['budgetmax', 'maxbudget'],
      configuration: ['configuration', 'bhk'],
      notes: ['notes', 'comments'],
    };
    setColumnMap(
      Object.fromEntries(
        importFields.map((field) => [
          field,
          headers.find((header) =>
            aliases[field].includes(header.toLowerCase().replace(/[^a-z]/g, '')),
          ) || '',
        ]),
      ),
    );
    setPreview(null);
  }, [headerKey]);
  async function run(commit = false) {
    if (busy || (commit && !preview)) return;
    const fingerprint = currentInput.current;
    setBusy(true);
    setError('');
    try {
      const res = await (commit ? mutate : api)(
        commit ? '/imports/commit' : '/imports/preview',
        'POST',
        commit
          ? preview.input
          : { csv: mapCsvColumns(raw, columnMap), sourceClass: source, source: sourceName },
      );
      if (commit) {
        notify('Validated relationships imported');
        onClose();
      } else if (currentInput.current === fingerprint)
        setPreview({
          ...res,
          input: { csv: mapCsvColumns(raw, columnMap), sourceClass: source, source: sourceName },
        });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Bring your relationships together"
      description="Preview, validate, and preserve the source of every connection."
      onClose={onClose}
      wide
    >
      <div className="form-grid">
        <label className="field">
          <span>Source classification</span>
          <select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPreview(null);
            }}
          >
            {['unknown', 'first_party', 'referral', 'public_business', 'licensed_enrichment'].map(
              (s) => (
                <option key={s}>{s}</option>
              ),
            )}
          </select>
        </label>
        <label className="field">
          <span>Source name / reference</span>
          <input
            value={sourceName}
            onChange={(e) => {
              setSourceName(e.target.value);
              setPreview(null);
            }}
          />
        </label>
        <label className="field field-full">
          <span>Upload a CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={async (e) => {
              const read = ++fileRead.current;
              const f = e.target.files?.[0];
              setPreview(null);
              if (f) {
                if (f.size > 2_000_000) {
                  setError('Choose a CSV file under 2 MB.');
                  return;
                }
                try {
                  const value = await f.text();
                  if (read === fileRead.current) {
                    setRaw(value);
                    setPreview(null);
                    setError('');
                  }
                } catch {
                  if (read === fileRead.current)
                    setError('The selected file could not be read. Choose it again.');
                }
              }
            }}
          />
        </label>
        <label className="field field-full">
          <span>CSV content · name, email, phone, company</span>
          <textarea
            rows={7}
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setPreview(null);
            }}
          />
        </label>
      </div>
      {headers.length > 0 && (
        <div className="import-mapping">
          <SectionHeading
            title="Connect your columns"
            subtitle="Match each source column to its place in the CRM. Unmapped columns stay out."
          />
          <div className="form-grid">
            {importFields.map((field) => (
              <label className="field" key={field}>
                <span>
                  {
                    (
                      {
                        name: 'Full name',
                        email: 'Email address',
                        phone: 'Phone number',
                        company: 'Company',
                        localities: 'Localities (separated by |)',
                        budgetMin: 'Minimum budget (₹)',
                        budgetMax: 'Maximum budget (₹)',
                        configuration: 'Configuration',
                        notes: 'Notes',
                      } as Record<string, string>
                    )[field]
                  }
                </span>
                <select
                  value={columnMap[field] || ''}
                  onChange={(e) => {
                    setColumnMap({ ...columnMap, [field]: e.target.value });
                    setPreview(null);
                  }}
                >
                  <option value="">Do not import</option>
                  {headers.map((header, index) => (
                    <option key={index} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>
      )}
      {preview && (
        <div className="import-preview">
          <h3>Validation preview</h3>
          <p className="muted">
            {preview.ready} ready to import · {preview.duplicates} duplicate identities ·{' '}
            {preview.invalid} need correction
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ROW</th>
                  <th>RELATIONSHIP</th>
                  <th>RESULT</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows?.slice(0, 30).map((r: any) => (
                  <tr key={r.row}>
                    <td>{r.row}</td>
                    <td>
                      <strong>{r.data?.name || 'Missing name'}</strong>
                      <div className="muted">{r.data?.email || r.data?.phone}</div>
                    </td>
                    <td>
                      <Badge
                        tone={
                          r.status === 'ready' ? 'green' : r.status === 'invalid' ? 'red' : 'amber'
                        }
                      >
                        {r.status}
                      </Badge>
                      {r.error && <p className="form-error">{r.error}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {error && <p className="form-error">{error}</p>}
      <div className="form-note">
        Unknown-source records are held from activation. Imports never create communication
        permission.
      </div>
      <div className="modal-footer">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn" disabled={busy} onClick={() => run(false)}>
          Validate & preview
        </button>
        <button
          className="btn btn-primary"
          disabled={!preview || busy || preview.invalid > 0}
          onClick={() => run(true)}
        >
          Import valid records <ArrowRight size={15} />
        </button>
      </div>
    </Modal>
  );
}

export function PipelinePage() {
  const { data, openContact, openCreate, mutate, notify } = useCRM();
  const [query, setQuery] = useState('');
  const [view, setView] = useState('Board');
  const [move, setMove] = useState<{ opportunity: Entity; stage: string } | null>(null);
  const [reason, setReason] = useState('');
  const [drag, setDrag] = useState<string | null>(null);
  const [filter, setFilter] = useState('Active deals');
  const [selectedId, setSelectedId] = useState('');
  const selected = data.opportunities.find((o) => o.id === selectedId);
  const visibleStages =
    filter === 'All stages'
      ? stages
      : filter === 'Closed deals'
        ? ['Closed Won', 'Closed Lost', 'Disqualified']
        : ['New', 'Qualified', 'Advisor Engaged', 'Site Visit', 'Negotiation'];
  const grouped = (stage: string) =>
    data.opportunities.filter(
      (o) =>
        (o.stage === stage ||
          (filter === 'Active deals' &&
            stage === 'New' &&
            ['Validated', 'Engaged', 'Nurture'].includes(o.stage))) &&
        `${o.title} ${data.contacts.find((c) => c.id === o.contactId)?.name}`
          .toLowerCase()
          .includes(query.toLowerCase()),
    );
  async function change(opportunity: Entity, stage: string, lostReason = '') {
    try {
      await mutate(`/opportunities/${opportunity.id}/stage`, 'POST', {
        stage,
        lostReason,
        reason: lostReason,
        version: opportunity.version,
      });
      notify(`Opportunity moved to ${stage}`);
      setMove(null);
      setReason('');
    } catch {}
  }
  function requestMove(o: Entity, stage: string) {
    if (!canWriteEntity(data.user, 'opportunities', o)) return;
    if (['Closed Lost', 'Disqualified'].includes(stage)) setMove({ opportunity: o, stage });
    else void change(o, stage);
  }
  return (
    <>
      <PageHeader
        eyebrow="FROM FIRST HELLO TO KEYS IN HAND"
        title="Your deal pipeline"
        description="A clear perspective on every opportunity, and the next move that matters."
        action={
          <button
            className="btn btn-primary"
            disabled={!canWriteEntity(data.user, 'opportunities')}
            onClick={() => openCreate('opportunities')}
          >
            <Plus size={16} />
            New opportunity
          </button>
        }
      />
      <div className="pipeline-toolbar">
        <div className="search-field">
          <Search size={16} />
          <input
            placeholder="Find an opportunity..."
            aria-label="Search opportunities"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Tabs
          items={['Active deals', 'Closed deals', 'All stages']}
          value={filter}
          onChange={setFilter}
        />
        <Tabs items={['Board', 'List']} value={view} onChange={setView} />
      </div>
      {view === 'Board' ? (
        <div className="kanban-board">
          {visibleStages.map((stage, i) => (
            <section
              key={stage}
              className={`kanban-column ${drag ? 'drag-ready' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const o = data.opportunities.find((o) => o.id === drag);
                if (o && o.stage !== stage) requestMove(o, stage);
                setDrag(null);
              }}
            >
              <div className="kanban-heading">
                <span>
                  <i
                    style={{
                      background: ['#b7c7f6', '#c5af83', '#849deb', '#557aed', '#2456ff'][i % 5],
                    }}
                  />
                  {stage}
                  <b>{grouped(stage).length}</b>
                </span>
                <button
                  aria-label={`Add ${stage} opportunity`}
                  onClick={() => openCreate('opportunities', { stage })}
                >
                  <Plus size={15} />
                </button>
              </div>
              <div className="kanban-value">
                {money(grouped(stage).reduce((s, o) => s + o.value, 0))}
              </div>
              <div className="kanban-cards">
                {grouped(stage).map((o) => {
                  const c = data.contacts.find((c) => c.id === o.contactId);
                  const property = data.properties.find((p) => p.id === o.propertyId);
                  return (
                    <motion.article
                      layout
                      key={o.id}
                      className={`deal-card ${drag === o.id ? 'dragging' : ''}`}
                      draggable={canWriteEntity(data.user, 'opportunities', o)}
                      onDragStart={() => setDrag(o.id)}
                      onDragEnd={() => setDrag(null)}
                    >
                      <div className="deal-card-top">
                        <span className="deal-category">
                          {property?.locality || c?.localities?.[0] || 'HYDERABAD'}
                        </span>
                        <GripVertical size={14} />
                      </div>
                      <button className="deal-title" onClick={() => setSelectedId(o.id)}>
                        {o.title}
                      </button>
                      <div className="deal-contact">
                        <Avatar name={c?.name || 'Buyer'} size={24} />
                        <span>{c?.name}</span>
                        {c && <Score value={c.score} />}
                      </div>
                      <div className="deal-value">
                        {money(o.value)}
                        <span>{o.probability}% probability</span>
                      </div>
                      <div className="deal-property">
                        <Building2 size={13} />
                        {property?.name || 'Discovering the right property'}
                      </div>
                      <div className="deal-next">
                        <Clock size={13} />
                        <span>{o.nextAction || 'Plan the next conversation'}</span>
                      </div>
                      <div className="deal-footer">
                        <Avatar name={o.owner || 'Advisor'} size={25} />
                        <small>{dateLabel(o.nextActionAt)}</small>
                        <select
                          aria-label={`Move ${o.title} to stage`}
                          disabled={!canWriteEntity(data.user, 'opportunities', o)}
                          value={o.stage}
                          onChange={(e) => requestMove(o, e.target.value)}
                        >
                          {stages.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </motion.article>
                  );
                })}
                <button
                  className="kanban-add"
                  onClick={() => openCreate('opportunities', { stage })}
                >
                  <Plus size={15} />
                  Add opportunity
                </button>
              </div>
            </section>
          ))}
        </div>
      ) : (
        <Panel>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>OPPORTUNITY</th>
                  <th>BUYER</th>
                  <th>VALUE</th>
                  <th>STAGE</th>
                  <th>PROBABILITY</th>
                  <th>NEXT ACTION</th>
                </tr>
              </thead>
              <tbody>
                {visibleStages
                  .flatMap((s) => grouped(s))
                  .map((o) => (
                    <tr key={o.id}>
                      <td>
                        <button className="text-link" onClick={() => setSelectedId(o.id)}>
                          {o.title}
                        </button>
                      </td>
                      <td>{data.contacts.find((c) => c.id === o.contactId)?.name}</td>
                      <td>{money(o.value)}</td>
                      <td>
                        <select
                          aria-label={`Move ${o.title} to stage`}
                          disabled={!canWriteEntity(data.user, 'opportunities', o)}
                          value={o.stage}
                          onChange={(e) => requestMove(o, e.target.value)}
                        >
                          {stages.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td>{o.probability}%</td>
                      <td>{o.nextAction}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      {selected && !move && (
        <Modal
          title={selected.title}
          description="A clear view of the relationship, the numbers, and the negotiation."
          wide
          onClose={() => setSelectedId('')}
        >
          <div className="opportunity-detail-head">
            <Badge tone="green">{selected.stage}</Badge>
            <span>Advised by {selected.owner}</span>
            <button
              className="btn btn-sm"
              onClick={() => {
                const contact = data.contacts.find((c) => c.id === selected.contactId);
                setSelectedId('');
                if (contact) openContact(contact);
              }}
            >
              Open relationship <ArrowUpRight size={13} />
            </button>
            <button
              className="btn btn-sm"
              onClick={() => {
                setSelectedId('');
                openCreate('opportunities', selected);
              }}
            >
              Edit opportunity
            </button>
          </div>
          <div className="opportunity-financials">
            <div>
              <span>Expected transaction</span>
              <strong>{money(selected.value)}</strong>
            </div>
            <div>
              <span>Expected commission</span>
              <strong>{money(selected.expectedRevenue)}</strong>
            </div>
            <div>
              <span>Win probability</span>
              <strong>{selected.probability}%</strong>
            </div>
          </div>
          <div className="opportunity-next-action">
            <Clock size={17} />
            <div>
              <strong>{selected.nextAction || 'No next action recorded'}</strong>
              <span>
                {dateLabel(selected.nextActionAt)} · Stage entered{' '}
                {dateLabel(selected.stageEnteredAt)}
              </span>
            </div>
          </div>
          <Suspense fallback={<p className="muted">Opening the negotiation history…</p>}>
            <OffersPanel opportunity={selected} />
          </Suspense>
        </Modal>
      )}
      {move && (
        <Modal
          title={`Move to ${move.stage}`}
          description="Capture the reason so every outcome becomes a useful insight."
          onClose={() => setMove(null)}
        >
          <label className="field">
            <span>Outcome reason</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">Select a reason</option>
              {[
                'Budget mismatch',
                'Location mismatch',
                'Timing changed',
                'Purchased elsewhere',
                'Financing unavailable',
                'No response',
                'Duplicate enquiry',
                'Other',
              ].map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <div className="modal-footer">
            <button className="btn" onClick={() => setMove(null)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={!reason}
              onClick={() => change(move.opportunity, move.stage, reason)}
            >
              Record outcome
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

export function PropertiesPage() {
  const { data, openCreate, notify, navigate, mutate, refresh } = useCRM();
  const [query, setQuery] = useState('');
  const [locality, setLocality] = useState('All localities');
  const [type, setType] = useState('All properties');
  const [selected, setSelected] = useState<Entity | null>(null);
  const [view, setView] = useState('Grid');
  const [syncing, setSyncing] = useState(false);
  const [syncPreview, setSyncPreview] = useState<any>(null);
  const activeProperties = data.properties.filter((p) => !isArchivedProperty(p));
  const archivedCount = data.properties.length - activeProperties.length;
  const properties = data.properties.filter(
    (p) =>
      `${p.name} ${p.developer} ${p.locality}`.toLowerCase().includes(query.toLowerCase()) &&
      (locality === 'All localities' || p.locality === locality) &&
      (type === 'Archived'
        ? isArchivedProperty(p)
        : !isArchivedProperty(p) &&
          (type === 'All properties' ||
            (type === 'Available' && isReadyProperty(p, Date.now(), data.settings.staleDays)) ||
            (type === 'Needs attention' &&
              !isReadyProperty(p, Date.now(), data.settings.staleDays)))),
  );
  async function sync() {
    if (syncing || !canWriteEntity(data.user, 'properties')) return;
    setSyncing(true);
    try {
      const res = await api('/properties/sync', 'POST', { dryRun: true });
      setSyncPreview(res);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setSyncing(false);
    }
  }
  return (
    <>
      <PageHeader
        eyebrow="CURATED SPACES. CONSIDERED LIVING."
        title="The property portfolio"
        description="Exceptional homes, verified details, and the intelligence to find their people."
        action={
          <>
            <button
              className="btn"
              onClick={sync}
              disabled={syncing || !canWriteEntity(data.user, 'properties')}
            >
              <RefreshCw size={15} className={syncing ? 'spin' : ''} />
              Sync inventory
            </button>
            <button
              className="btn btn-primary"
              disabled={!canWriteEntity(data.user, 'properties')}
              onClick={() => openCreate('properties')}
            >
              <Plus size={16} />
              Add property
            </button>
          </>
        }
      />
      <InventorySyncStatus refreshKey={data.properties} onRefresh={refresh} />
      <div className="portfolio-banner">
        <span className="portfolio-emblem">
          <Building2 size={24} />
        </span>
        <div>
          <h3>A portfolio built on possibility.</h3>
          <p>
            {activeProperties.length} current properties across{' '}
            {new Set(activeProperties.map((p) => p.locality)).size} localities.
            {archivedCount > 0 &&
              ` ${archivedCount} archived ${archivedCount === 1 ? 'listing retained' : 'listings retained'} for history.`}
          </p>
        </div>
        <span>
          <ShieldCheck size={15} />
          Source & freshness tracked
        </span>
      </div>
      <div className="properties-toolbar">
        <Tabs
          items={['All properties', 'Available', 'Needs attention', 'Archived']}
          value={type}
          onChange={setType}
        />
        <div className="filter-right">
          <div className="search-field">
            <Search size={16} />
            <input
              placeholder="Search the portfolio"
              aria-label="Search properties"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select
            aria-label="Filter locality"
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
          >
            <option>All localities</option>
            {Array.from(new Set(data.properties.map((p) => p.locality))).map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
          <div className="view-toggle">
            <button
              className={view === 'Grid' ? 'active' : ''}
              aria-label="Grid view"
              onClick={() => setView('Grid')}
            >
              <LayoutGrid size={17} />
            </button>
            <button
              className={view === 'List' ? 'active' : ''}
              aria-label="List view"
              onClick={() => setView('List')}
            >
              <List size={17} />
            </button>
          </div>
        </div>
      </div>
      <div className={`property-grid ${view === 'List' ? 'property-list' : ''}`}>
        {properties.map((p) => (
          <motion.article
            key={p.id}
            layout
            className="property-card"
            whileHover={{ y: -4 }}
            transition={{ duration: 0.22 }}
          >
            <button className="property-image" onClick={() => setSelected(p)}>
              <PropertyImage property={p} />
              <span className="property-type">{p.type}</span>
              <span
                className={`property-status ${p.availability === 'Available' ? 'available' : ''}`}
              >
                <i />
                {propertyStatusLabel(p)}
              </span>
              <span className="property-image-link">
                <ArrowUpRight size={18} />
              </span>
            </button>
            <div className="property-card-content">
              <span className="property-locality">
                <MapPin size={12} />
                {p.locality}, Hyderabad
              </span>
              <button className="property-name" onClick={() => setSelected(p)}>
                {p.name}
              </button>
              <p className="property-developer">by {p.developer}</p>
              <div className="property-features">
                <span>
                  <BedDouble size={15} />
                  {p.bhk}
                </span>
                <i />
                <span>
                  <Maximize size={13} />
                  {propertyAreaLabel(p)}
                </span>
              </div>
              <div className="property-card-footer">
                <div>
                  <small>ASKING PRICE</small>
                  <strong>{propertyPriceLabel(p)}</strong>
                </div>
                <button className="btn btn-sm" onClick={() => setSelected(p)}>
                  Discover <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="property-freshness">
                <ShieldCheck size={12} />
                {p.verifiedAt
                  ? `Verified ${dateLabel(p.verifiedAt)}`
                  : 'Availability not yet verified'}
                <span>Quality {p.qualityScore}%</span>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
      {!properties.length && (
        <EmptyState
          title="A different perspective awaits"
          description="Try another locality or clear your filters."
        />
      )}
      {selected && (
        <PropertyDialog
          property={data.properties.find((p) => p.id === selected.id) || selected}
          onClose={() => setSelected(null)}
        />
      )}
      {syncPreview && (
        <Modal
          title="Review your inventory refresh"
          description="Review property changes from your inventory source before updating the portfolio."
          onClose={() => setSyncPreview(null)}
          wide
        >
          <div className="metric-strip">
            <div>
              <strong>{syncPreview.total}</strong>
              <span>Source records</span>
            </div>
            <div>
              <strong>{syncPreview.ready}</strong>
              <span>Valid records</span>
            </div>
            <div>
              <strong>
                {syncPreview.changes.filter((x: any) => x.action === 'create').length}
              </strong>
              <span>New properties</span>
            </div>
            <div>
              <strong>{syncPreview.invalid.length}</strong>
              <span>Need review</span>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>PROPERTY</th>
                  <th>LOCALITY</th>
                  <th>PRICE</th>
                  <th>CHANGE</th>
                </tr>
              </thead>
              <tbody>
                {syncPreview.changes.map((row: any) => (
                  <tr key={row.sourceRef}>
                    <td>
                      {row.name}
                      {row.catalogOnly && <small>Catalog · needs review</small>}
                    </td>
                    <td>{row.locality}</td>
                    <td>{propertyPriceLabel(row)}</td>
                    <td>
                      <Badge tone={row.action === 'create' ? 'green' : 'amber'}>{row.action}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {syncPreview.invalid.map((row: any) => (
            <p key={row.sourceRef} className="form-error">
              {row.sourceRef}: {row.reason}
            </p>
          ))}
          <div className="form-note">
            Invalid records stay out of the portfolio. Changes to availability refresh
            recommendations and create follow-up tasks for affected opportunities.
            {syncPreview.changes.some((row: any) => row.catalogOnly) && (
              <p>
                Listings with incomplete price, area or availability are kept as catalog records.
                They need review before buyer matching.
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn" onClick={() => setSyncPreview(null)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={
                syncing ||
                (syncPreview.ready === 0 && !syncPreview.changes.length) ||
                !canWriteEntity(data.user, 'properties')
              }
              onClick={async () => {
                setSyncing(true);
                try {
                  const res = await mutate('/properties/sync', 'POST', {
                    dryRun: false,
                    ...(syncPreview.previewHash ? { previewHash: syncPreview.previewHash } : {}),
                  });
                  setSyncPreview(null);
                  notify(
                    `${res.created} new properties, ${res.updated} updated, ${res.unchanged} unchanged${res.archived ? `, ${res.archived} archived` : ''}`,
                  );
                } catch {
                } finally {
                  setSyncing(false);
                }
              }}
            >
              <RefreshCw size={15} />
              Apply inventory refresh
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
function PropertyDialog({ property: p, onClose }: { property: Entity; onClose: () => void }) {
  const { data, openContact, openCreate } = useCRM();
  const [buyers, setBuyers] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    setBuyers([]);
    api(`/intelligence/reverse-matches/${p.id}`)
      .then((r) => {
        if (alive) setBuyers(r.data || r.matches || r);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [p.id]);
  return (
    <Modal
      title={p.name}
      description={`${p.locality}, Hyderabad · ${p.developer}`}
      onClose={onClose}
      wide
    >
      <div className="property-detail-image">
        <PropertyImage property={p} />
        <strong>{propertyPriceLabel(p)}</strong>
      </div>
      <div className="property-detail-facts">
        <span>
          <BedDouble size={18} />
          <strong>{p.bhk}</strong>Configuration
        </span>
        <span>
          <Maximize size={18} />
          <strong>{propertyAreaLabel(p)}</strong>Area
        </span>
        <span>
          <CalendarDays size={18} />
          <strong>{p.possession}</strong>Possession
        </span>
        <span>
          <ShieldCheck size={18} />
          <strong>{p.qualityState}</strong>Quality state
        </span>
      </div>
      <p className="detail-description">{p.sourceDetails?.description || p.description}</p>
      {p.catalogOnly && (
        <div className="form-note">
          <strong>
            {p.inventoryKind === 'project'
              ? 'Project catalog · individual units required'
              : 'Catalog listing · needs review'}
          </strong>
          <p>
            Confirm the listing details before including this property in buyer recommendations.
          </p>
          {Array.isArray(p.sourceIssues) && p.sourceIssues.length > 0 && (
            <ul>
              {p.sourceIssues.map((issue: string, index: number) => (
                <li key={`${issue}-${index}`}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="amenity-tags">
        {(p.sourceDetails?.amenities || p.amenities)?.map((a: string, index: number) => (
          <Badge key={`${a}-${index}`}>{a}</Badge>
        ))}
      </div>
      <PropertySourceDetails details={p.sourceDetails} updatedAt={p.sourceUpdatedAt} />
      <div className="detail-source">
        <span>
          RERA <strong>{p.rera || 'Not recorded'}</strong>
        </span>
        <span>
          Last verified <strong>{dateLabel(p.verifiedAt)}</strong>
        </span>
        <span>
          Source <strong>{p.sourceRef}</strong>
        </span>
      </div>
      <SectionHeading
        title="People who could call this home"
        subtitle="Reverse matches from verified requirements."
      />
      {p.catalogOnly ? (
        <p className="muted">
          Buyer matching is paused until this catalog listing has been reviewed.
        </p>
      ) : loading ? (
        <p className="muted" role="status">
          Checking buyer matches…
        </p>
      ) : error ? (
        <p className="form-error">{error}</p>
      ) : buyers.length ? (
        buyers.slice(0, 4).map((b: any) => {
          const c = b.contact || data.contacts.find((c) => c.id === b.contactId);
          return (
            c && (
              <button
                className="match-buyer-row"
                key={c.id}
                onClick={() => {
                  onClose();
                  openContact(c);
                }}
              >
                <Avatar name={c.name} />
                <span>
                  <strong>{c.name}</strong>
                  <small>
                    {c.configuration} · {contactBudgetLabel(c)}
                  </small>
                </span>
                <Score value={b.score} />
                <ArrowUpRight size={16} />
              </button>
            )
          );
        })
      ) : (
        <p className="muted">No buyers currently meet the hard constraints for this property.</p>
      )}
      <div className="modal-footer">
        <button
          className="btn"
          disabled={!canWriteEntity(data.user, 'properties')}
          onClick={() => {
            onClose();
            openCreate('properties', p);
          }}
        >
          Edit property
        </button>
        <button
          className="btn btn-primary"
          disabled={!canWriteEntity(data.user, 'visits')}
          onClick={() => {
            onClose();
            openCreate('visits', { propertyId: p.id });
          }}
        >
          <CalendarDays size={15} />
          Arrange a viewing
        </button>
      </div>
    </Modal>
  );
}

export function MatchesPage() {
  const { data, openContact, openCreate, mutate, notify } = useCRM();
  const [id, setId] = useState(data.contacts[0]?.id || '');
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const contact = data.contacts.find((c) => c.id === id);
  const canEdit = canWriteEntity(data.user, 'contacts', contact);
  useEffect(() => {
    if (!contact) setId(data.contacts.find((c) => !c.suppressed)?.id || '');
  }, [contact, data.contacts]);
  useEffect(() => {
    let alive = true;
    if (!id) {
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    api(`/intelligence/matches/${id}`)
      .then((r) => {
        if (alive) setMatches(r.data || r.matches || r);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, contact?.version, data.properties]);
  async function record(propertyId: string, value: string) {
    if (!contact || !canEdit) return;
    try {
      await mutate('/intelligence/feedback', 'POST', {
        contactId: id,
        propertyId,
        feedback: value,
        reason: value,
      });
      notify('Your feedback will inform the next recommendation');
    } catch {}
  }
  async function toggleLock(match: any) {
    if (!contact || !canEdit) return;
    try {
      await mutate(`/contacts/${id}/shortlist`, 'POST', {
        propertyId: match.propertyId,
        locked: !match.locked,
        reason: match.locked
          ? 'Advisor released this shortlist entry'
          : 'Advisor retained this property for buyer review',
      });
      notify(
        match.locked
          ? 'Shortlist entry unlocked'
          : 'Property pinned; future inventory warnings will remain visible',
      );
    } catch {}
  }
  return (
    <>
      <PageHeader
        eyebrow="INTELLIGENCE WITH A HUMAN TOUCH"
        title="The matching studio"
        description="The perfect fit isn't a coincidence. It's a considered connection."
        action={
          <Badge tone="green" dot>
            Explainable matching · v1
          </Badge>
        }
      />
      <div className="matching-layout">
        <Panel className="matching-buyers">
          <SectionHeading
            title="Find their next chapter"
            subtitle="Choose a buyer to explore their matches."
          />
          <div className="search-field">
            <Search size={15} />
            <input
              placeholder="Find a buyer..."
              aria-label="Search buyers"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="buyer-selector">
            {data.contacts
              .filter(
                (c) =>
                  !c.suppressed &&
                  `${c.name} ${c.localities?.join(' ')}`
                    .toLowerCase()
                    .includes(query.toLowerCase()),
              )
              .map((c) => (
                <button
                  className={c.id === id ? 'active' : ''}
                  key={c.id}
                  onClick={() => setId(c.id)}
                >
                  <Avatar name={c.name} />
                  <span>
                    <strong>{c.name}</strong>
                    <small>
                      {c.localities?.[0]} · {c.configuration}
                    </small>
                  </span>
                  <Score value={c.score} />
                </button>
              ))}
          </div>
        </Panel>
        <div className="matching-results">
          {contact && (
            <Panel className="match-context">
              <div>
                <Avatar name={contact.name} size={48} />
                <span>
                  <h2>{contact.name}'s possibilities</h2>
                  <p>
                    {contact.purpose || 'Purpose not recorded'} · {contact.configuration} ·{' '}
                    {contactBudgetLabel(contact)}
                  </p>
                </span>
                <button
                  className="icon-btn"
                  aria-label="Open buyer profile"
                  onClick={() => openContact(contact)}
                >
                  <ArrowUpRight size={18} />
                </button>
              </div>
              <div className="match-context-tags">
                <Badge>
                  <MapPin size={11} />
                  {contact.localities?.join(' / ')}
                </Badge>
                <Badge>
                  <ShieldCheck size={11} />
                  Hard constraints checked
                </Badge>
                <Badge>
                  <Clock size={11} />
                  Pinned entries retain warnings
                </Badge>
              </div>
            </Panel>
          )}
          {loading ? (
            <div className="matching-loading">
              <Loader2 className="spin" />
              <p>Connecting requirements with possibilities...</p>
            </div>
          ) : error ? (
            <EmptyState title="Matches couldn't be loaded" description={error} />
          ) : matches.length ? (
            matches.map((m, i) => {
              const p = m.property || data.properties.find((p) => p.id === m.propertyId);
              if (!p) return null;
              return (
                <motion.article
                  className={`match-card ${m.eligible === false ? 'ineligible' : ''}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  key={p.id}
                >
                  <div className="match-card-image">
                    <PropertyImage property={p} />
                    <span>#{i + 1} MATCH</span>
                  </div>
                  <div className="match-card-body">
                    <div className="match-card-title">
                      <div>
                        <span className="eyebrow">{p.locality}</span>
                        <h3>{p.name}</h3>
                        {m.locked && (
                          <span className="locked-match-label">
                            <Pin size={11} /> Advisor-pinned shortlist
                          </span>
                        )}
                      </div>
                      <div className="match-score-ring" style={{ '--score': `${m.score}%` } as any}>
                        <strong>
                          {m.eligible === false ? '!' : Math.round(m.score)}
                          <small>{m.eligible === false ? 'RECHECK' : '% FIT'}</small>
                        </strong>
                      </div>
                    </div>
                    <p className="match-card-meta">
                      {p.bhk} <i /> {propertyAreaLabel(p)} <i />{' '}
                      <strong>{propertyPriceLabel(p)}</strong>
                    </p>
                    <div className="match-reasons">
                      {m.reasons?.map((r: string) => (
                        <span key={r}>
                          <Check size={12} />
                          {r.replaceAll('_', ' ').toLowerCase()}
                        </span>
                      ))}
                    </div>
                    {m.warnings?.length > 0 && (
                      <p className="match-warning">{m.warnings.join(' · ')}</p>
                    )}
                    <div className="match-card-actions">
                      <div>
                        <button
                          className={`btn btn-sm ${m.feedback === 'relevant' ? 'btn-soft' : ''}`}
                          onClick={() => record(p.id, 'relevant')}
                          disabled={!canEdit}
                        >
                          <Heart
                            size={13}
                            fill={m.feedback === 'relevant' ? 'currentColor' : 'none'}
                          />
                          {m.feedback === 'relevant' ? 'Shortlisted' : 'Shortlist'}
                        </button>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => record(p.id, 'irrelevant')}
                          disabled={!canEdit}
                        >
                          Not a fit
                        </button>
                        <button
                          className={`btn btn-sm ${m.locked ? 'btn-soft' : 'btn-ghost'}`}
                          onClick={() => toggleLock(m)}
                          disabled={!canEdit}
                          aria-label={`${m.locked ? 'Unpin' : 'Pin'} ${p.name}`}
                        >
                          {m.locked ? <PinOff size={13} /> : <Pin size={13} />}
                          {m.locked ? 'Unpin' : 'Pin'}
                        </button>
                      </div>
                      <button
                        className="text-link"
                        disabled={m.eligible === false || !canWriteEntity(data.user, 'visits')}
                        onClick={() => openCreate('visits', { contactId: id, propertyId: p.id })}
                      >
                        Plan a viewing <ArrowUpRight size={14} />
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })
          ) : (
            <EmptyState
              icon={Compass}
              title="Let's refine the search"
              description="No available properties satisfy this buyer's hard constraints. Update their requirements or add fresh inventory."
              action={
                contact && (
                  <button className="btn" onClick={() => openContact(contact)}>
                    Review requirements
                  </button>
                )
              }
            />
          )}
          <p className="match-footnote">
            <ShieldCheck size={12} />
            Ranked by budget, locality, configuration, timing and verified availability. No
            sensitive attributes.
          </p>
        </div>
      </div>
    </>
  );
}
